import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useMemo,
  useCallback, useState,
} from 'react';
import { FormattedMessage } from 'react-intl';

import {
  useBind,
  useSnackbar,
  useCancellablePromise,
  usePhoneNumberValidation,
  useSmartCaptcha,
} from '../../../../../../hooks';
import { PhoneStepForm } from './phone-step-form';
import { yup } from '../../../../../../services/yup';
import {
  useActionsInProgress,
} from '../../../../../../graphql/preloader/actions/actions-in-progress';
import {
  IStepperImperativeHandleProps,
  IStepComponentProps,
  IFormImperativeHandleProps,
} from '../../../../../common';
import {
  IStepsState,
  TimerStatusOptions,
  TUserNameValues,
} from '../../reset-password.models';
import { consumerApi, RecaptchaActions } from '../../../../../../rest/consumer';
import { IExternalStateProps } from '../../reset-password.models';
import { useResetPasswordError } from '../../hooks/use-reset-password-error';
import { CaptchaType } from '../../../../../../graphql/configuration';
import { handleReCaptcha } from '../../../../../../utils';
import { CAPTCHA_CHECK_ERROR, PHONE_LENGTH } from '../../../../../../constants';
import { setResetPhoneStepValues } from '../../../../../../graphql/user/user.cache';
import { useReactiveVar } from '@apollo/client';

export const PhoneStep = forwardRef<IStepperImperativeHandleProps, IStepComponentProps>(({
  stepsState,
  onGoNextSuccess,
  onGoNextFail,
  onGoNext,
  isNavigationAllowed,
  externalState,
}, ref): JSX.Element => {
  const formRef = useRef<IFormImperativeHandleProps>(null);
  const { username: phoneStepState } = stepsState as IStepsState;
  const {
    makeCancellablePromise,
    CancelledPromiseOnUnmountError,
  } = useCancellablePromise();

  const { enqueueSnackbar } = useSnackbar();
  const { addActionInProgress, removeActionInProgress } = useActionsInProgress();

  const userNameValidation = usePhoneNumberValidation();

  const validationSchema = useMemo(() => yup.object({
    username: userNameValidation,
  }), [userNameValidation]);

  useImperativeHandle(ref, () => ({
    goNext() {
      formRef.current!.submit();
    },
    goBack() {
      return {
        formValues: formRef.current!.getValues(),
      };
    },
  }));

  const defaultValues = useMemo(
    () => phoneStepState || {
      username: '',
    },
    [phoneStepState],
  );

  const makeCancellablePromiseBind = useBind(makeCancellablePromise);
  const CancelledPromiseOnUnmountErrorBind = useBind(CancelledPromiseOnUnmountError);
  const onGoNextSuccessBind = useBind(onGoNextSuccess);
  const onGoNextFailBind = useBind(onGoNextFail);
  const onGoNextBind = useBind(onGoNext);
  const enqueueSnackbarBind = useBind(enqueueSnackbar);
  const isNavigationAllowedBind = useBind(isNavigationAllowed);
  const addActionInProgressBind = useBind(addActionInProgress);
  const removeActionInProgressBind = useBind(removeActionInProgress);

  const { start, reset, status: timerStatus, time, captcha } = externalState as IExternalStateProps;

  const phoneStepValues = useReactiveVar(setResetPhoneStepValues);

  const handleAfterSend = (value: string) => {
    setResetPhoneStepValues({
      username: value,
    });

    removeActionInProgressBind.current();

    enqueueSnackbarBind.current(
      <FormattedMessage id="resetPassword.successRequest"/>,
      { variant: 'success' },
    );

    reset();
    start();

    onGoNextSuccessBind.current({ username: value });
  };

  const handlePhoneSend = useCallback(async (username: string, token?: string) => {
    await makeCancellablePromiseBind.current(consumerApi.sendPinCodeCaptcha({ username }, token || '', captcha.type));

    handleAfterSend(username);
  }, [captcha, makeCancellablePromiseBind, handleAfterSend]);

  const { handleError } = useResetPasswordError();

  // SmartCaptcha
  const { smartToken, isVisible, handleSmartCaptcha, showCaptcha, clearToken, isReset, handleReset, hideCaptcha } = useSmartCaptcha();

  const handleSubmitError = useCallback((error: any): void => {
    removeActionInProgressBind.current();

    if (smartToken && captcha?.type === CaptchaType.smartCaptcha) {
      clearToken();
    }

    if (error instanceof CancelledPromiseOnUnmountErrorBind.current) {
      return;
    }

    handleError(error);
    onGoNextFailBind.current();
  }, [
    removeActionInProgressBind,
    handleError,
    onGoNextFailBind,
    CancelledPromiseOnUnmountErrorBind,
    captcha,
    clearToken,
    smartToken,
  ]);

  // Continue without submit if timer is running
  const handleNoSubmit = useCallback(({ username }: TUserNameValues): void => {
    onGoNextBind.current();
    onGoNextSuccessBind.current({
      ...phoneStepValues,
      username,
    });
  }, []);

  const [isNewNumber, setIsNewNumber] = useState<boolean>(false);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

  const handlePhoneChange = useCallback((): void => {
    // Clear the captcha token when phone number changes
    // User needs to complete captcha again for the new phone number
    if (smartToken && captcha?.type === CaptchaType.smartCaptcha && !captcha.isVisibleCaptcha) {
      clearToken();
    }

    if ((formRef?.current?.getValues()?.username as string)?.length !== PHONE_LENGTH) {
      setIsSubmitEnabled(false);
    } else {
      setIsSubmitEnabled(true);
    }

    if (formRef?.current?.getValues() && timerStatus === TimerStatusOptions.running) {
      const { username: phoneFieldNewValue } = formRef.current.getValues();
      const phone = phoneStepState.username;

      if (phoneFieldNewValue !== phone) {
        setIsNewNumber(true);
      }

      if (phoneFieldNewValue === phone) {
        setIsNewNumber(false);
      }
    }

    // disableSubmitButton();
  }, [setIsNewNumber, formRef, phoneStepState?.username, smartToken, captcha, clearToken, setIsSubmitEnabled]);

  const isSubmitDisabled = useMemo((): boolean => {
    return phoneStepValues?.username?.length
      ? isNewNumber
        ? !isSubmitEnabled
        : phoneStepValues?.username?.length !== PHONE_LENGTH && formRef?.current?.getValues()?.username !== PHONE_LENGTH
      : !isSubmitEnabled;
  }, [phoneStepValues, isSubmitEnabled, formRef?.current?.getValues()?.username, isNewNumber]);

  // This function is called when the SmartCaptcha challenge is hidden (either passed or closed)
  const handleChallengeHidden = useCallback((): void => {
    if (!smartToken) {
      clearToken();
      hideCaptcha();
    }
  }, [smartToken]);

  const handleSubmit = useCallback(async ({ username }: TUserNameValues) => {
    if (!isNavigationAllowedBind.current) {
      return;
    }

    // Smart Captcha - Invisible mode
    if (captcha && captcha?.isEnabled && captcha.type === CaptchaType.smartCaptcha && !captcha.isVisibleCaptcha) {
      showCaptcha(async (token: string) => {
        onGoNextBind.current();
        addActionInProgressBind.current();

        try {
          await handlePhoneSend(username, token);
          clearToken();
          handleReset(true);
        } catch (error: any) {
          handleSubmitError(error);
          handleReset(true);
        }
      });

      return;
    }

    // Smart Captcha - Visible mode
    if (captcha && captcha?.isEnabled && captcha.type === CaptchaType.smartCaptcha && captcha.isVisibleCaptcha) {
      if (!smartToken) {
        enqueueSnackbarBind.current(CAPTCHA_CHECK_ERROR, { variant: 'error' });
        return;
      }

      onGoNextBind.current();
      addActionInProgressBind.current();

      try {
        await handlePhoneSend(username, smartToken);
        clearToken();
        handleReset(true);
      } catch (error: any) {
        handleSubmitError(error);
        handleReset(true);
      }

      return;
    }

    // For non-SmartCaptcha flows, proceed normally
    onGoNextBind.current();
    addActionInProgressBind.current();

    try {
      // reCaptcha
      if (captcha && captcha?.isEnabled && captcha.type === CaptchaType.reCaptcha) {
        handleReCaptcha(
          (token: string) => handlePhoneSend(username, token),
          handleSubmitError,
          RecaptchaActions.resetPassword,
        );
      } else {
        await makeCancellablePromiseBind.current(consumerApi.sendPinCode({ username }));

        handleAfterSend(username);
      }
    } catch (error: any) {
      handleSubmitError(error);
    }
  }, [
    reset,
    start,
    timerStatus,
    onGoNextBind,
    onGoNextSuccessBind,
    isNavigationAllowedBind,
    makeCancellablePromiseBind,
    addActionInProgressBind,
    captcha,
    handleSubmitError,
    handlePhoneSend,
    showCaptcha,
    clearToken,
    smartToken,
    enqueueSnackbarBind,
    handleAfterSend,
    handleReCaptcha,
    handleReset,
  ]);

  return (
    <PhoneStepForm
      ref={formRef}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      defaultValues={defaultValues}
      time={time}
      isTimerRunning={timerStatus === TimerStatusOptions.running}
      onPhoneChange={handlePhoneChange}
      onSuccess={handleSmartCaptcha}
      isVisible={isVisible}
      captcha={captcha}
      isResetCaptcha={isReset}
      onNoSubmit={handleNoSubmit}
      isNewNumber={isNewNumber}
      isSubmitDisabled={isSubmitDisabled}
      onChallengeHidden={handleChallengeHidden}
    />
  );
});
